/**
 * A review gate with no pull request behind it (issue #120).
 *
 * The worker opens the pull request as its last step, and that step can fail on
 * its own: a base branch deleted mid-run, `gh` unauthenticated, no push access.
 * The work is pushed and its gates passed, so the run is a success and the hub
 * moves the step into `review` - whose gate is an "Open PR" button. Handing a
 * person that button with nothing behind it is the contradiction these two
 * helpers close: the gate says instead what happened and how to finish it.
 *
 * Read off the run summary rather than a new field, because the summary is
 * where the worker already writes the reason, and because a run reported before
 * this change carries the same sentence and should read the same way.
 */

/**
 * Notes are joined with a blank line, and the reason can wrap several lines of
 * git's own complaint, so a paragraph is the unit rather than a line.
 */
const NO_PULL_REQUEST = [
  // What the worker writes when `gh pr create` refused. The older wording is
  // kept because runs reported before this change carry it.
  /opening the (?:pull request|PR) failed/i,
  // The three settings that mean there was never going to be one.
  /WORKER_OPEN_PR is off/i,
  /WORKER_PUSH is off/i,
  /no origin remote/i,
];

/** Why this step has no pull request, in the worker's words, or null if it never said. */
export function noPullRequestReason(summary: string | null | undefined): string | null {
  if (!summary) return null;
  const paragraph = summary
    .split(/\n{2,}/)
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '')
    .findLast((entry) => NO_PULL_REQUEST.some((pattern) => pattern.test(entry)));
  return paragraph ?? null;
}

/**
 * The command that opens it by hand, ready to paste into the repo's checkout.
 *
 * No `--base`: `gh` targets the repository's default branch, which is where a
 * step whose stacked base was deleted belongs anyway - the parent's commits are
 * in the default branch once its own pull request merged. `--fill` takes the
 * title and body from the branch's commits, so nothing has to be retyped.
 */
export function ghPrCreateCommand(branch: string): string {
  return `gh pr create --head ${branch} --fill`;
}
